// src/pages/LittleEllyPage/LittleEllyPage.js
import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faGraduationCap,
    faUsers,
    faBookOpen,
    faChild,
    faChalkboardTeacher,
    faRunning,
    faShieldAlt,
    faStar,
    faHeart,
    faBaby,
    faClock,
    faHandHoldingHeart,
    faSmile,
    faComments,
    faPuzzlePiece,
    faSun,
    faCloud,
    faTree,
    faIceCream
} from '@fortawesome/free-solid-svg-icons';
import Navbar from '../../components/common/Navbar/Navbar';
import Footer from '../../components/common/Footer/Footer';
import Button from '../../components/common/Button/Button';
import ScrollToTop from '../../components/common/ScrollToTop/ScrollToTop';
import useScrollReveal from '../../hooks/useScrollReveal';
import LEKudluLogo from '../../assets/images/LEKudluLogoTSP.svg';
import './LittleEllyPage.css';

const LittleEllyPage = () => {
    useScrollReveal();

    // Program data
    const programs = [
        {
            id: "toddler",
            title: "Toddler",
            icon: faBaby,
            ageGroup: "18 months +",
            ratio: "Adult to child ratio - 1:5",
            description: "The toddler curriculum at Little Elly is well structured and designed to encourage the holistic development of children who are in the process of rapid physical and cognitive development.",
            skills: [
                "Constructive play",
                "Sensory exploration",
                "Mark making learning",
                "Artistic movement",
                "Emergent Literacy"
            ]
        },
        {
            id: "playgroup",
            title: "Play Group",
            icon: faChild,
            ageGroup: "2 years +",
            ratio: "Adult to child ratio - 2:15",
            description: "The Playgroup Program at Little Elly focuses on happy learning evolving in the school environment. We concentrate on the wholesome development of the child by conducting age–appropriate activities.",
            skills: [
                "Pre-writing skills",
                "Cognitive development",
                "Social and emotional learning",
                "Gross and fine motor skills",
                "Language and communication skills"
            ]
        },
        {
            id: "nursery",
            title: "Nursery",
            icon: faBookOpen,
            ageGroup: "3 years +",
            ratio: "Adult to child ratio - 2:18",
            description: "The Nursery Program at Little Elly is well-equipped with concepts that mould our little tots as inquisitive learners. The education journey of a child is fostered through the love for learning.",
            skills: [
                "Communication Skills",
                "Dramatic play and role play",
                "Story Time",
                "Visual stimulation through play",
                "Literacy and Math concepts"
            ]
        },
        {
            id: "lkg",
            title: "LKG",
            icon: faGraduationCap,
            ageGroup: "4 years +",
            ratio: "Adult to child ratio - 2:20",
            description: "The LKG program at Little Elly caters to our curious learners. Here the focus for kids is to learn concepts through hands-on learning and to encourage them to express themselves by building a strong vocabulary.",
            skills: [
                "Mindful activities",
                "Spoken language",
                "Social skills and creative arts",
                "Pre-reading skills",
                "General awareness"
            ]
        },
        {
            id: "ukg",
            title: "UKG",
            icon: faUsers,
            ageGroup: "5 years +",
            ratio: "Adult to child ratio - 2:20",
            description: "The UKG program at Little Elly focuses on building the foundational skills to prepare children to learn more advanced concepts and develop independent reading and writing skills.",
            skills: [
                "Independent reading and writing skills",
                "Mathematical concepts",
                "Advance phonics skills",
                "Logical learning",
                "Problem solving skills"
            ]
        }
    ];

    // Features data
    const features = [
        {
            icon: faShieldAlt,
            title: "Safe Environment",
            description: "A secure and nurturing space designed specifically for young learners."
        },
        {
            icon: faChalkboardTeacher,
            title: "Qualified Educators",
            description: "Experienced teachers passionate about early childhood education."
        },
        {
            icon: faHeart,
            title: "Child-Centered Approach",
            description: "Activities tailored to each child's unique needs and learning style."
        },
        {
            icon: faRunning,
            title: "Physical Development",
            description: "Spaces and activities to develop gross and fine motor skills."
        }
    ];

    // Animated shapes for background
    const renderShapes = () => {
        const shapes = [
            { icon: faSun, color: "#FFD600", size: "3rem", top: "15%", left: "5%" },
            { icon: faCloud, color: "#90CAF9", size: "2.5rem", top: "10%", right: "8%" },
            { icon: faTree, color: "#66BB6A", size: "2.8rem", bottom: "15%", left: "7%" },
            { icon: faPuzzlePiece, color: "#FF7043", size: "2rem", top: "30%", right: "5%" },
            { icon: faIceCream, color: "#E91E63", size: "2.3rem", bottom: "10%", right: "10%" },
            { icon: faStar, color: "#7C4DFF", size: "2.2rem", top: "40%", left: "3%" }
        ];

        return shapes.map((shape, index) => (
            <div
                key={index}
                className="floating-shape"
                style={{
                    color: shape.color,
                    fontSize: shape.size,
                    top: shape.top,
                    left: shape.left || "auto",
                    right: shape.right || "auto",
                    bottom: shape.bottom || "auto",
                    animationDelay: `${index * 0.3}s`
                }}
            >
                <FontAwesomeIcon icon={shape.icon} />
            </div>
        ));
    };

    return (
        <div className="little-elly-page">
            <div className="background-shapes">
                {renderShapes()}
            </div>

            <Navbar theme="little-elly" />

            <main>
                {/* Header Section */}
                <section className="school-header">
                    <div className="rainbow-border"></div>
                    <div className="container">
                        <div className="school-intro reveal">
                            <div className="school-logo-wrapper">
                                <div className="purple-circle"></div>
                                <div className="school-logo">
                                    <img src={LEKudluLogo} alt="Little Elly Kudlu Logo" />
                                </div>
                            </div>
                            <h1>Little Elly Kudlu</h1>
                            <p className="tagline">Where young minds blossom through play and discovery</p>
                        </div>
                    </div>
                </section>

                {/* About Section */}
                <section className="school-about">
                    <div className="container">
                        <div className="about-content reveal">
                            <div className="about-text">
                                <div className="section-title-decoration">
                                    <span className="circle-dot"></span>
                                    <span className="circle-dot"></span>
                                    <span className="circle-dot"></span>
                                </div>
                                <h2>About Our Preschool</h2>
                                <p>
                                    Little Elly Kudlu is a premier preschool and kindergarten where young learners develop foundational skills through joyful, creative, and engaging experiences. We provide a nurturing environment that encourages curiosity, creativity, and confidence in children aged 18 months to 6 years.
                                </p>
                                <div className="key-features">
                                    {features.map((feature, index) => (
                                        <div className="feature-item" key={index}>
                                            <div className="feature-icon">
                                                <FontAwesomeIcon icon={feature.icon} />
                                            </div>
                                            <div className="feature-content">
                                                <h3>{feature.title}</h3>
                                                <p>{feature.description}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="cta-box">
                                <h3>Join Our Little Elly Family</h3>
                                <p>Visit our campus to experience our vibrant learning environment firsthand.</p>
                                <Button
                                    variant="primary"
                                    size="large"
                                    to="/"
                                    onClick={() => {
                                        // Navigate to home page and then scroll to contact section after a short delay
                                        setTimeout(() => {
                                            const contactSection = document.getElementById('contact');
                                            if (contactSection) {
                                                const yOffset = -80; // Adjust for navbar height
                                                const y = contactSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
                                                window.scrollTo({ top: y, behavior: 'smooth' });
                                            }
                                        }, 300); // Short delay to ensure the page loads
                                    }}
                                >
                                    Schedule a visit by contacting us.
                                </Button>
                                <div className="contact-options">
                                    <div className="chat-option">
                                        <FontAwesomeIcon icon={faComments} />
                                        <span>Register your interest via our chatbot below</span>
                                    </div>
                                    <div className="daycare-notice">
                                        <FontAwesomeIcon icon={faClock} />
                                        <span>Daycare services available with flexible admissions year-round</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Programs Section */}
                <section className="school-programs" id="programs">
                    <div className="cloud-decoration cloud-left"></div>
                    <div className="cloud-decoration cloud-right"></div>
                    <div className="container">
                        <div className="section-header reveal">
                            <h2>Our Programs</h2>
                            <div className="title-underline"></div>
                            <p>Tailored educational pathways for each stage of early childhood development</p>
                        </div>

                        <div className="program-selector reveal">
                            <div className="program-tabs">
                                {programs.map((program) => (
                                    <a href={`#${program.id}`} key={program.id} className="program-tab">
                                        <FontAwesomeIcon icon={program.icon} />
                                        <span>{program.title}</span>
                                    </a>
                                ))}
                            </div>
                        </div>

                        <div className="programs-grid reveal">
                            {programs.map((program, index) => (
                                <div className={`program-card card-${index % 5}`} id={program.id} key={program.id}>
                                    <div className="program-header">
                                        <div className="program-icon-wrapper">
                                            <FontAwesomeIcon icon={program.icon} className="program-icon" />
                                        </div>
                                        <h3>{program.title}</h3>
                                        <div className="program-age">
                                            <span className="age">{program.ageGroup}</span>
                                            <span className="ratio">{program.ratio}</span>
                                        </div>
                                    </div>
                                    <div className="program-body">
                                        <p>{program.description}</p>
                                        <div className="skills-section">
                                            <h4>Skills Developed:</h4>
                                            <ul className="skills-list">
                                                {program.skills.map((skill, index) => (
                                                    <li key={index}>
                                                        <FontAwesomeIcon icon={faStar} className="skill-icon" />
                                                        <span>{skill}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Daycare Section */}
                <section className="daycare-section">
                    <div className="container">
                        <div className="daycare-content reveal">
                            <div className="daycare-icon">
                                <FontAwesomeIcon icon={faHandHoldingHeart} />
                            </div>
                            <h2>Daycare Facility</h2>
                            <div className="title-decoration">
                                <span className="decoration-dot"></span>
                                <span className="decoration-dot"></span>
                                <span className="decoration-dot"></span>
                            </div>
                            <p>Our daycare facilities provide a child-friendly environment with a stimulating curriculum that includes various interest-focused activities for different age groups.</p>

                            <div className="daycare-features">
                                <div className="daycare-feature">
                                    <FontAwesomeIcon icon={faChalkboardTeacher} />
                                    <span>Trained and competent staff</span>
                                </div>
                                <div className="daycare-feature">
                                    <FontAwesomeIcon icon={faUsers} />
                                    <span>Open door policy for parents</span>
                                </div>
                                <div className="daycare-feature">
                                    <FontAwesomeIcon icon={faGraduationCap} />
                                    <span>Headed by a visionary leadership team</span>
                                </div>
                                <div className="daycare-feature">
                                    <FontAwesomeIcon icon={faSmile} />
                                    <span>Flexible admissions year-round</span>
                                </div>
                            </div>

                            <div className="daycare-cta">
                                <Button
                                    variant="primary"
                                    to="/"
                                    onClick={() => {
                                        setTimeout(() => {
                                            const contactSection = document.getElementById('contact');
                                            if (contactSection) {
                                                const yOffset = -80;
                                                const y = contactSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
                                                window.scrollTo({ top: y, behavior: 'smooth' });
                                            }
                                        }, 300);
                                    }}
                                >
                                    Enquire About Daycare
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Admission Section */}
                <section className="admission-section">
                    <div className="container">
                        <div className="admission-content reveal">
                            <h2>Join Little Elly Kudlu</h2>
                            <div className="balloon-decoration">
                                <div className="balloon balloon-1"></div>
                                <div className="balloon balloon-2"></div>
                                <div className="balloon balloon-3"></div>
                            </div>
                            <p>Begin your child's educational journey with us. Our admissions team is ready to guide you through the process and answer all your questions.</p>

                            <div className="admission-buttons">
                                <Button
                                    variant="primary"
                                    size="large"
                                    to="/"
                                    onClick={() => {
                                        setTimeout(() => {
                                            const contactSection = document.getElementById('contact');
                                            if (contactSection) {
                                                const yOffset = -80;
                                                const y = contactSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
                                                window.scrollTo({ top: y, behavior: 'smooth' });
                                            }
                                        }, 300);
                                    }}
                                >
                                    Contact Admissions
                                </Button>
                                <Link
                                    to="/summer-camp"
                                    className="summer-camp-link"
                                    onClick={() => window.scrollTo(0, 0)}
                                >
                                    Explore our Summer Camp →
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <Footer theme="little-elly" />
            <ScrollToTop theme="little-elly" />
        </div>
    );
};

export default LittleEllyPage;